/**
 * The only thing in this app that talks to the API.
 *
 * Two properties it is responsible for, both of which matter more than they
 * look:
 *
 * IDEMPOTENCY. Every write carries a client-generated UUID, and the server
 * writes it with a condition on that id not already existing. A retry after a
 * dropped response is then a no-op rather than a second $20 in the ledger.
 * `write()` generates the id once and reuses it across retries -- generating
 * a fresh one per attempt would defeat the entire mechanism, which is the
 * easy way to get this wrong.
 *
 * NO MONEY ARITHMETIC. There is none in this file and none anywhere else in
 * apps/web. Splitting evenly to the cent with the remainder on the first
 * share is implemented once, in services/api/valley/domain.py, ported from
 * apps/display/server.py. Two implementations drift, and when they drift the
 * family's money is wrong.
 */

import { assertKnownOrigin, config } from '../config';
import { accessToken } from '../auth/cognito';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** A client-generated id for a write, so a retry cannot double-count. */
export function newWriteId(): string {
  return crypto.randomUUID();
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | undefined>;
  /** Retries reuse this. Never regenerate it between attempts. */
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(config.apiBase + path);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, value);
  }
  assertKnownOrigin(url.toString());

  const token = await accessToken();
  if (!token) {
    throw new ApiError('Please sign in again.', 401);
  }

  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    // No cookies, ever. This API is bearer-token only, so there is no ambient
    // credential for a cross-site request to ride on -- which is why there is
    // no CSRF token here, unlike the cookie-authenticated display server.
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal) init.signal = options.signal;

  const response = await fetch(url.toString(), init);

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const message =
      typeof payload === 'object' && payload !== null && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : 'Something went wrong. Please try again.';
    throw new ApiError(message, response.status);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: Record<string, string | undefined>) =>
    request<T>(path, query ? { query } : {}),

  /**
   * A write, with its idempotency id fixed for the life of the call.
   *
   * `id` is generated here rather than by the caller so that no screen can
   * forget it, and so a caller retrying by calling again gets a genuinely new
   * entry -- which is what they meant -- while a transport-level retry inside
   * one call reuses the same id.
   */
  write: <T>(method: 'POST' | 'PUT' | 'DELETE', path: string, body: Record<string, unknown> = {}) =>
    request<T>(path, {
      method,
      body: method === 'DELETE' ? body : { id: newWriteId(), ...body },
    }),
};
